import React, { useEffect, useState, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Stack from 'react-bootstrap/Stack';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { CustomScroll } from "react-custom-scroll";
import Label from 'components/formElements/Label';
import RadioButton from 'components/formElements/RadioButton';
import Switch from 'components/formElements/Switch';
import ResultBarcard from 'components/formElements/ResultBarcard';
import ResultCounterCard from 'components/formElements/ResultCounterCard';
import Accordion from 'components/formElements/Accordion';
import IconLightning from 'assets/images/icon_lightning.svg';
import IconEuro from 'assets/images/icon_euro.svg';
import { FormContext } from 'context/FormContext';
import DevicePlaceholderImage from 'assets/images/device_placeholder_thumbnail.png';
import DevicePlaceholderThumbnail from 'assets/images/device_placeholder_thumbnail.png';
import { createPDF } from 'services/simulatorApiProxy';
import staticDeviceList from 'data/staticDeviceList';
import {
    sumTotal,
    privateConsumptionYearly,
    privateConsumptionPercentage,
    getInstalledKWp,
    annualSavings,
    annualSavingsOver20Years,
    annualFeedInRevenue,
    incomeOver20Years
} from 'services/calculationHelper';

import { calcAi360ChartData } from 'components/PDFElements/AI360Chart';
import AI360Image from 'assets/images/ai360.png';

const mountingList = {
    foot: 'Standfuß',
    wallmount: 'Wandhalterung'
}

const colorList = {
    white: 'Weiß',
    black: 'Schwarz'
}

const SimulationResult = () => {

    const navigate = useNavigate();
    const [layer, setLayer] = useState(0);
    const [selectedStorageModel, setSelectedStorageModel] = useState(null);
    const [selectedStorageConfiguration, setSelectedStorageConfiguration] = useState(null);
    const [fadeOut, setFadeOut] = useState(false);
    const { formValues, setFormValues, resultValues, batteryTypes, storageConfiguration } = useContext(FormContext);
    const isInitialSetup = useRef(true);
    const [creatingPDF, setCreatingPDF] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [showAiSavings, setShowAiSavings] = useState(false);

    // on init set selectedStorageModel based of result
    useEffect(() => {
        if (!isInitialSetup.current) {
            return;
        }
        var entry = Object.entries(storageConfiguration).find(([key, value]) => value.model === resultValues.storage);
        if (!entry) {
            // Fallback
            entry = ["XSERIE", storageConfiguration.XSERIE];
        }
        const [series, model] = entry;
        setSelectedStorageModel({ series: series, data: model });

    }, [storageConfiguration]);

    useEffect(() => {

        if (!selectedStorageModel) return;

        if (isInitialSetup.current) {
            // Initial setup
            const resultBatteryCapacity = parseFloat(resultValues.battery.split(' ')[1]);
            const resultBatteryType = resultValues.battery.split(' ')[0];

            setSelectedStorageConfiguration({
                series: selectedStorageModel.series,
                model: selectedStorageModel.data,
                battery: {
                    type: resultBatteryType,
                    capacity: resultBatteryCapacity
                },
                mounting: selectedStorageModel.data.mounting[0],
                color: selectedStorageModel.data.color[0],
            });

            isInitialSetup.current = false;
        } else {
            // Normal User-Flow (wenn User aktiv ein anderes Model auswählt)
            setSelectedStorageConfiguration({
                series: selectedStorageModel.series,
                model: selectedStorageModel.data,
                battery: {
                    type: selectedStorageModel.data.bat?.[0]?.batType,
                    capacity: selectedStorageModel.data.availableCapacity[0]
                },
                mounting: selectedStorageModel.data.mounting[0],
                color: selectedStorageModel.data.color[0],
            });
        }

        const timeout = setTimeout(() => setFadeOut(false), 200);
        return () => clearTimeout(timeout);
    }, [selectedStorageModel]);

    useEffect(() => {      
        setFormValues(prev => ({
            ...prev,
            storageConfiguration: selectedStorageConfiguration
        }));
    }, [selectedStorageConfiguration]);

    const deviceImage = (key) => {
        if (staticDeviceList[key]) {
            return staticDeviceList[key].image;
        }
        return DevicePlaceholderImage;
    }

    const deviceThumbnail = (key) => {
        if (staticDeviceList[key]) {
            return staticDeviceList[key].thumbnail;
        }
        return DevicePlaceholderThumbnail;
    }

    const getTagBySeries = (key) => {
        if (key === 'ONE') {
            return 'Outdoor fähig';
        }
        if (key === 'XFLEX') {
            return 'Expertentipp';
        }
        if (key === 'XFLEXCOMPACT') {
            return 'Expertentipp';
        }        
        return null;
    }    

    const getHighlightBySeries = (key) => {
        if (key === 'ONE') {
            return true;
        }
        if (key === 'XFLEX') {
            return true;
        }
        if (key === 'XFLEXCOMPACT') {
            return true;
        }        
        return false;
    }    

    const handleDeviceClick = (key) => {
        const model = storageConfiguration[key];
        setFadeOut(true);
        const timeout = setTimeout(() => {
            setSelectedStorageModel({
                series: key,
                data: model
            });
        }, 100);
        return () => clearTimeout(timeout);
    }

    const handleBatteryCapacityChange = (capacity) => {
        // console.log('handleBatteryCapacityChange')
        setSelectedStorageConfiguration({
            ...selectedStorageConfiguration,
            battery: {
                ...selectedStorageConfiguration?.battery,
                capacity: capacity
            }
        });
    }

    const handleMountingChange = (mounting) => {
        // console.log('handleMountingChange')
        setSelectedStorageConfiguration({
            ...selectedStorageConfiguration,
            mounting: mounting
        });
    }

    const handleColorChange = (color) => {
        // console.log('handleColorChange')
        setSelectedStorageConfiguration({
            ...selectedStorageConfiguration,
            color: color
        });
    }

    const batteryCapacity = () => {        
        return `${selectedStorageConfiguration?.battery?.capacity} kWh`;
    }

    const startSimulation = () => {
        setFormValues(prev => ({
            ...prev,
            forceSimulationConfig: true
        }));
        navigate('/SimulationRequest');
    }

    const handlePDFDownload = async () => {
        setCreatingPDF(true);
        try {
            // await createPDF(resultValues.id);
            const pdfData = await createPDF(resultValues.id);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }));
            link.download = 'simulation.pdf';
            link.click();
        } catch (error) {
            console.error('Error during request:', error);
        } finally {
            setCreatingPDF(false);
        }
    }

    const Device = (props) => {
        return (
            <div onClick={() => props.onClick(props.series)} className={`device cursor-pointer${props.highlight ? ' highlight' : ''}`}>
                <div className="content">
                    <div>
                        <div className="badge bg-green mb-2">{props.tag}</div>
                    </div>                
                    <img src={props.image} />                
                    <div>{props.name}</div>
                    <div className="sub">{props.subtitle}</div>
                </div>
                <hr />
            </div>)
    }

    const privateConsumptionTotal = privateConsumptionYearly(
        sumTotal(resultValues.houseConsumptionMonthly),
        sumTotal(resultValues.gridSupplyMonthly)
    );

    const feedInTotal = sumTotal(resultValues.feedInMonthly);
    const solarProductionTotal = sumTotal(resultValues.solarProductionMonthly);

    const ai360ChartData = calcAi360ChartData({
        cars: formValues.Cars,
        electricityPrice: parseInt(formValues.Energycost),
        totalConsumptionMonthly: resultValues.totalConsumptionMonthly,
        gridSupplyMonthly: resultValues.gridSupplyMonthly,
        autarky: parseInt(resultValues.autarkyYear),
        batteryCapacity: parseFloat(getInstalledKWp(formValues.Roof.Zones).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
        wallboxConsumption: 0,
        heatPumpConsumption: formValues.HeatingPumpConsumption || 0,
        houseConsumption: formValues.AnnualConsumption
    });

    const annualSavingsAI360 = sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly) - sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly_module3);
    const annualSavingsAI360Over20Years = annualSavingsAI360 * 20;

    function getTotalValue({
        privateConsumptionTotal,
        energyCost,
        feedInTotal,
        energyCostTwentyYearIncrease,
        energyCompensation,
        showAiSavings,
        annualSavingsAI360Over20Years,
        }) {
        let total =
            incomeOver20Years(
            privateConsumptionTotal,
            energyCost,
            feedInTotal,
            energyCostTwentyYearIncrease,
            energyCompensation
            );

        if (showAiSavings) {
            total += annualSavingsAI360Over20Years;
        }

        return total;
    }

    return (
        <Row className="SimulationResultWrapper flex-column flex-xl-row">
            <Col className="DeviceSelection order-2 order-md-2 order-xl-3 order-xxl-1">
                <CustomScroll heightRelativeToParent="100%">
                    {Object.keys(storageConfiguration).length === 0 ? (
                        <div className="flex justify-center items-center h-full">
                            <span>Loading...</span>
                        </div>
                    ) : (
                        Object.entries(storageConfiguration).map(([key, value]) => (
                            <Device
                                key={key}
                                onClick={handleDeviceClick}
                                series={key}
                                title={value.model}
                                tag={getTagBySeries(key)}
                                highlight={getHighlightBySeries(key)}
                                name={value.model}
                                image={deviceThumbnail(key)}
                            />
                        )))}
                </CustomScroll>
            </Col>
            <Col className="SelectedDevice d-flex order-1 order-md-1 order-xl-1 order-xxl-2">
                <Stack gap={2} className={`DeviceRecommendation ContainerPaddingSmall text-center ${fadeOut ? 'fade-out' : 'fade-in'}`} data-show={layer}>
                    {!selectedStorageModel || !selectedStorageConfiguration ? (
                        <div>
                            {/* <img src={ImageLoading} className="LoadingAnimation" alt="Loading..." width="150" height="150" /> */}
                            Loading...
                        </div>
                    ) : (
                        <>
                            {selectedStorageModel.data.model === resultValues.storage &&
                                <small>Unsere Empfehlung</small>
                            }
                            <h1>{selectedStorageModel.data.model}</h1>
                            <small>{batteryCapacity()} (nutzbar)</small>
                            <img src={deviceImage(selectedStorageModel.series)} alt="Gerät" />
                            <div
                                onClick={(e) => {
                                    e.preventDefault();
                                    setLayer(1);
                                }}
                                className="openSettings"
                            >
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.3525 12.7073C21.3525 12.1974 21.311 11.6945 21.228 11.2126L24.0002 8.54452L21.7327 4.57732L18.0548 5.6669C17.2944 5.03131 16.4302 4.52145 15.49 4.17222L14.5982 0.414551H10.0631L9.16442 4.17222C8.23113 4.52843 7.36006 5.0383 6.5996 5.6669L2.92867 4.57034L0.661133 8.54452L3.42643 11.2056C3.34347 11.6945 3.30199 12.1974 3.30199 12.7073C3.30199 13.2172 3.34347 13.7131 3.42643 14.202L0.661133 16.8701L2.92867 20.8373L6.5996 19.7477C7.36006 20.3833 8.23113 20.8931 9.16442 21.2424L10.0631 24.993H14.5913L15.49 21.2424C16.4302 20.8862 17.2944 20.3763 18.0548 19.7477L21.7258 20.8443L23.9933 16.8701L21.228 14.202C21.311 13.7131 21.3525 13.2172 21.3525 12.7073ZM12.3307 18.0504C9.35107 18.0504 6.93144 15.6059 6.93144 12.5955C6.93144 9.58522 9.35107 7.14064 12.3307 7.14064C15.3103 7.14064 17.7299 9.58522 17.7299 12.5955C17.7299 15.6059 15.3103 18.0504 12.3307 18.0504Z" fill="#5CC244" /></svg> Gerät individualisieren
                            </div>
                            <Row className="mt-4">
                                <Col>
                                    <Button variant="success" onClick={startSimulation} className="mt-0">Neu simulieren</Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </Stack>
                <Stack className={`DeviceSettings text-center ContainerPaddingSmall ${fadeOut ? 'fade-out' : 'fade-in'}`} data-show={layer}>
                    {selectedStorageModel && (
                        <>
                            <h1>Konfiguration</h1>
                            {/* dont use cols, use something more flexible */}
                            <Stack gap={3} direction="horizontal" className="flex-wrap mt-4">
                                <div className="mt-0">
                                    <Label text="Speicherkapazität* (kWh)" />
                                    <div className="d-flex flex-wrap gap-3 mt-3">
                                        {selectedStorageModel.data.availableCapacity.map((capacity) => (
                                            <RadioButton
                                                small
                                                key={capacity}
                                                name="batteryCapacity"
                                                label={capacity}
                                                value={capacity}
                                                onChange={() => handleBatteryCapacityChange(capacity)}
                                                checked={selectedStorageConfiguration?.battery?.capacity === capacity}
                                                disableContextAutoSave={true}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-0">
                                    <Label text="Elektromobilität" />
                                    <div className="d-flex gap-3 mt-3">
                                        {/* <Switch name="elektromobilitaet" initialChecked={true} /> */}
                                        <Switch name="elektromobilitaet" initialChecked={formValues.Cars.length > 0} />
                                    </div>
                                </div>

                                <div className="mt-0">
                                    <Label text="Notstromfunktion" />
                                    <div className="d-flex gap-3 mt-3">
                                        {/* <Switch name="notstromfunktion" initialChecked={true} /> */}
                                        <Switch name="notstromfunktion" initialChecked={formValues.EmergencyPower == 'Ja'} />
                                    </div>
                                </div>

                                <div className="mt-0">
                                    <Label text="Platzierung" />
                                    <div className="d-flex flex-wrap gap-3 mt-3">
                                        {selectedStorageModel.data.mounting.map((mounting) => (
                                            <RadioButton
                                                small
                                                key={mounting}
                                                value={mounting}
                                                name="mounting"
                                                label={mountingList[mounting]}
                                                onChange={() => handleMountingChange(mounting)}
                                                checked={selectedStorageConfiguration?.mounting === mounting}
                                                disableContextAutoSave={true}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-0">
                                    <Label text="Farbe" />
                                    <div className="d-flex flex-wrap gap-3 mt-3">
                                        {selectedStorageModel.data.color.map((color) => (
                                            <RadioButton
                                                small
                                                key={color}
                                                value={color}
                                                name="color"
                                                label={colorList[color]}
                                                onChange={() => handleColorChange(color)}
                                                checked={selectedStorageConfiguration?.color === color}
                                                disableContextAutoSave={true}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </Stack>
                            <Row className="mt-3">
                                <Col>
                                    <Button variant="success" onClick={() => setLayer(0)} className="mt-5">Speichern</Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </Stack>
            </Col>
            <Col className="SimulationResult order-3 order-md-3 order-xl-2 order-xxl-3">
                <Stack gap={2} className="ContainerPaddingSmall" style={{ height: "100%" }}>
                    <small className="text-center">Ihr individuelles</small>
                    <h1 className="text-center">Simulationsergebnis</h1>
                    <div className="grid-3 mb-2">
                        <ResultBarcard value={parseInt(resultValues.autarkyYear)} title="Autarkie" description="&nbsp;" icon={IconLightning} />                                                
                        {/* <ResultBarcard value="100" textValue={incomeOver20Years(privateConsumptionTotal, formValues.Energycost, feedInTotal, formValues.EnergycostTwentyYearIncrease, formValues.EnergyCompensation).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} suffix="€" title="Gesamtvorteil" description="in 20 Jahren" icon={IconEuro} /> */}
                        <ResultBarcard
                            value="100"
                            textValue={getTotalValue({
                                privateConsumptionTotal,
                                energyCost: formValues.Energycost,
                                feedInTotal,
                                energyCostTwentyYearIncrease: formValues.EnergycostTwentyYearIncrease,
                                energyCompensation: formValues.EnergyCompensation,
                                showAiSavings,
                                annualSavingsAI360Over20Years,
                            }).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            suffix="€"
                            title="Gesamtvorteil"
                            description="in 20 Jahren"
                            icon={IconEuro}
                        />
                                                </div>
                    <div className="px-4" style={{ maxHeight: "340px", overflowY: "auto", scrollbarWidth: 'none'}}>
                        <div className="ResultCardGrid grid-3">
                            {
                                [
                                    { title: "Jahresertrag", value: sumTotal(resultValues.solarProductionMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }), suffix: "kWh" },
                                    { title: "Netzbezug", value: sumTotal(resultValues.gridSupplyMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }), suffix: "kWh" },
                                    { title: "Eigen-verbrauchsquote", value: privateConsumptionPercentage(privateConsumptionTotal, solarProductionTotal), suffix: "%" },
                                    { title: "Einspeisung", value: sumTotal(resultValues.feedInMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }), suffix: "kWh" },
                                    { title: "Jahresverbrauch", value: sumTotal(resultValues.houseConsumptionMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }), suffix: "kWh" },
                                    { title: "Installierte Solarleistung", value: getInstalledKWp(formValues.Roof.Zones).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "kWp" }
                                ].map((card, index) => (<ResultCounterCard key={index} title={card.title} value={card.value} suffix={card.suffix} />))
                            }
                            <div className="flex-full-width hide-scrollbar">
                            <Accordion isOpen={showMore}>
                                <div className="ResultCardGrid grid-3">
                                    {
                                        [
                                            { title: "Einspeisevergütung pro Jahr", value: annualFeedInRevenue(feedInTotal, formValues.EnergyCompensation).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "€" },
                                            { title: "Einsparung pro Jahr", value: annualSavings(privateConsumptionTotal, formValues.Energycost).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "€" },
                                            { title: "Einsparung in 20 Jahren", value: annualSavingsOver20Years(privateConsumptionTotal, formValues.Energycost, formValues.EnergycostTwentyYearIncrease).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "€" },
                                        ].map((card, index) => (<ResultCounterCard key={index} title={card.title} value={card.value} suffix={card.suffix} />))
                                    }                            
                                    <div className="resultCounterCard" style={{background: "none", textAlign: "left", paddingLeft: "10px", paddingRight: "10px"}}>
                                        {/* <Label text="Einsparpotential mit AI 360°" /> */}
                                        <div className='d-flex flex-row align-items-center mt-1'>
                                        <Label text="Zusätzliches Sparpotential" />
                                        <img src={AI360Image} className='ms-1' style={{ height: '40px' }} />
                                        </div>
                                        <div className="d-flex mt-2">
                                            <Switch
                                                name="ai_savings"
                                                initialChecked={showAiSavings}
                                                onToggle={() => setShowAiSavings(prev => !prev)}
                                            />
                                        </div>
                                    </div>                                    
                                    {showAiSavings &&
                                        [
                                            { title: "Einsparung mit AI 360°", value: annualSavingsAI360.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "€" },
                                            { title: "Einsparung mit AI 360° in 20 Jahren", value: annualSavingsAI360Over20Years.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), suffix: "€" },
                                        ].map((card, index) => (<ResultCounterCard key={index} title={card.title} value={card.value} suffix={card.suffix} />))
                                    }                                    
                                </div>
                            </Accordion>  
                            </div>                          
                        </div>
                        <div className="mt-4 text-end">
                            <button
                                onClick={() => setShowMore(prev => !prev)}
                                className="btn btn-outline-light"
                                type='button'
                            >
                                {showMore ? "Weniger Details" : "Weitere Details"}
                            </button>
                        </div>
                    </div>

                    <Stack gap={3} direction="horizontal" className="px-4 pt-4" style={{ marginTop: 'auto' }}>
                        <Link to="/"><Button variant="outline-light">Eingaben bearbeiten</Button></Link>
                        <Button
                            variant="success"
                            onClick={handlePDFDownload}
                            disabled={creatingPDF}
                        >
                            {creatingPDF ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Wird erstellt...
                                </>
                            ) : (
                                <>
                                    <svg className="me-2" width="30" height="20" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13.8579 14.1206C14.0079 14.2636 14.1865 14.3769 14.3833 14.4534C14.7772 14.6108 15.2209 14.6108 15.6148 14.4534C15.8116 14.3769 15.9902 14.2636 16.1402 14.1206L20.9759 9.51899C21.3715 9.12945 21.5215 8.57015 21.371 8.04787C21.2207 7.52531 20.7916 7.11729 20.2427 6.97399C19.6939 6.83096 19.1061 6.97373 18.6968 7.35018L16.6109 9.33484V1.53384C16.6109 0.985864 16.3038 0.479485 15.8051 0.205494C15.3064 -0.0684979 14.6917 -0.0684979 14.193 0.205494C13.6943 0.479485 13.3873 0.985857 13.3873 1.53384V9.33484L11.303 7.35169C10.8936 6.97524 10.3058 6.83247 9.75699 6.9755C9.20811 7.11879 8.77907 7.52681 8.62875 8.04938C8.47818 8.57168 8.62821 9.13095 9.02381 9.5205L13.8579 14.1206Z" fill="white" />
                                        <path d="M28.5432 12.8569C28.1568 12.8569 27.786 13.0248 27.5128 13.3233C27.2395 13.6217 27.0861 14.0266 27.0861 14.4489V19.2244C27.0861 19.6465 26.9324 20.0513 26.6592 20.3497C26.386 20.6485 26.0154 20.8161 25.6288 20.8161H5.22891C4.84225 20.8161 4.4717 20.6485 4.19848 20.3497C3.92527 20.0513 3.77158 19.6464 3.77158 19.2244V14.4489C3.77158 13.8802 3.49397 13.3547 3.04317 13.0704C2.59235 12.786 2.03666 12.786 1.58584 13.0704C1.13502 13.3547 0.857422 13.8802 0.857422 14.4489V19.2244C0.857422 20.4907 1.31799 21.7053 2.13764 22.601C2.95755 23.4967 4.06947 23.9998 5.22891 23.9998H25.6288C26.7882 23.9998 27.9002 23.4967 28.7201 22.601C29.5397 21.7053 30.0003 20.4907 30.0003 19.2244V14.4489C30.0003 14.0265 29.8468 13.6217 29.5736 13.3233C29.3004 13.0248 28.9296 12.8569 28.5432 12.8569Z" fill="white" />
                                    </svg>
                                    PDF Download
                                </>
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </Col>
        </Row>
    );
}

export default SimulationResult;